import { useState } from "react";
import { Check, ArrowUpRight, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { whatsappUrl } from "@/lib/whatsapp";
import { TIDYCAL_EMBED_URL, TIDYCAL_URL } from "@/lib/tidycal";
import { WhatsappGlyph } from "@/components/WhatsappFab";
import { BOUTIQUE_ENABLED } from "@/lib/features";

export default function DiagnosticSection() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", challenge: "", team_size: "",
  });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createLead(form);
      toast.success("Demande envoyée. Je te recontacte sous 24h.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", challenge: "", team_size: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi.";
      toast.error(typeof msg === "string" ? msg : "Vérifie les champs.");
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="diagnostic" className="bg-pearl py-24 sm:py-32" data-testid="diagnostic-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="font-mono-overline text-mute mb-4">Diagnostic gratuit</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight" data-testid="diagnostic-title">
              45 minutes. <em className="text-mute not-italic font-light">Un regard honnête.</em>
            </h2>
            <p className="mt-6 text-mute text-lg leading-relaxed">
              Aucune obligation. Aucune slide de vente. Juste un échange
              technique sur ton organisation, tes irritants, tes leviers.
              À la sortie, tu sais où tu en es — et ce que tu peux faire dès lundi.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "Cartographie rapide de tes 3 irritants principaux",
                "Repérage des biais d'organisation et goulots cognitifs",
                "2 ou 3 quick wins concrets (Lean, Neuro, IA)",
                "Avis sincère : on bosse ensemble ou pas",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check size={18} className="text-sage mt-1 flex-shrink-0" />
                  <span className="text-ink">{t}</span>
                </li>
              ))}
            </ul>

            {BOUTIQUE_ENABLED && (
              <div className="mt-10 p-6 bg-pearl-dark border border-ink/10">
                <div className="font-mono-overline text-mute mb-2">Bonus</div>
                <div className="font-serif text-xl mb-2">Guide IA opérationnelle 2025 — PDF</div>
                <p className="text-sm text-mute mb-4">38 pages, 12 cas d'usage chiffrés.</p>
                <a href="/produit/guide-ia-gratuit" className="inline-flex items-center gap-2 text-sm border-b border-ink pb-0.5 hover:gap-3 transition-all" data-testid="diagnostic-bonus-link">
                  Télécharger gratuitement <ArrowUpRight size={14} />
                </a>
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            {/* WhatsApp shortcut */}
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="diagnostic-whatsapp-cta"
              className="group flex items-center justify-between gap-6 p-6 mb-6 bg-[#0E2A14] hover:bg-[#0a2210] text-pearl border-l-4 border-[#25D366] transition-colors"
            >
              <div className="flex items-center gap-5">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366]">
                  <WhatsappGlyph size={26} />
                </span>
                <div>
                  <div className="font-mono-overline text-[#25D366] mb-1">Le plus rapide</div>
                  <div className="font-serif text-xl leading-tight">Écris-moi directement sur WhatsApp</div>
                  <p className="text-sm text-pearl/60 mt-1">Réponse personnelle sous quelques heures · sans formulaire</p>
                </div>
              </div>
              <ArrowUpRight size={22} className="text-pearl/70 group-hover:text-pearl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform flex-shrink-0" />
            </a>

            {/* TidyCal — book a slot directly */}
            <div className="bg-white border border-ink/10" data-testid="diagnostic-tidycal">
              <div className="flex items-center justify-between gap-4 p-5 border-b border-ink/10">
                <div className="flex items-center gap-3">
                  <CalendarCheck2 size={20} className="text-navy-deep" />
                  <div>
                    <div className="font-serif text-lg leading-tight">Réserve directement ton créneau</div>
                    <div className="text-xs text-mute mt-0.5">Choisis l'heure qui te va · pas de relance</div>
                  </div>
                </div>
                <a
                  href={TIDYCAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-1 text-xs text-mute hover:text-ink transition border-b border-ink/30 hover:border-ink pb-0.5"
                  data-testid="tidycal-open-tab"
                >
                  Ouvrir dans un onglet <ArrowUpRight size={12} />
                </a>
              </div>
              <iframe
                src={TIDYCAL_EMBED_URL}
                title="Réserver un diagnostic gratuit ShinRai"
                width="100%"
                height="780"
                style={{ border: 0, display: "block" }}
                loading="lazy"
              />
            </div>

            {/* Backup: form for those who don't want to commit to a slot yet */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowForm((v) => !v)}
                className="text-sm text-mute hover:text-ink transition inline-flex items-center gap-1 border-b border-ink/20 hover:border-ink pb-0.5"
                data-testid="diagnostic-form-toggle"
              >
                {showForm ? "← Masquer le formulaire" : "Tu préfères m'écrire par formulaire ?"}
              </button>
            </div>

            {showForm && (
              <form onSubmit={handleSubmit} className="bg-white p-8 sm:p-10 border border-ink/10 mt-6" data-testid="diagnostic-form">
                <h3 className="font-serif text-2xl mb-2">Réserver mon diagnostic par formulaire</h3>
                <p className="text-sm text-mute mb-8">
                  Réponse sous 24 heures ouvrées. Aucune carte bancaire requise.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-mono-overline text-mute mb-2 block">Prénom</label>
                    <input data-testid="form-firstname" className="shin-input" required value={form.first_name} onChange={update("first_name")} />
                  </div>
                  <div>
                    <label className="font-mono-overline text-mute mb-2 block">Nom</label>
                    <input data-testid="form-lastname" className="shin-input" required value={form.last_name} onChange={update("last_name")} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-mono-overline text-mute mb-2 block">Email professionnel</label>
                    <input data-testid="form-email" type="email" className="shin-input" required value={form.email} onChange={update("email")} />
                  </div>
                  <div>
                    <label className="font-mono-overline text-mute mb-2 block">Téléphone</label>
                    <input data-testid="form-phone" type="tel" className="shin-input" required value={form.phone} onChange={update("phone")} />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="font-mono-overline text-mute mb-2 block">Ton principal défi en ce moment</label>
                  <select data-testid="form-challenge" className="shin-input" required value={form.challenge} onChange={update("challenge")}>
                    <option value="">Sélectionner…</option>
                    <option>Trop de chaos opérationnel</option>
                    <option>Équipes désengagées / résistance au changement</option>
                    <option>Trop de tâches manuelles répétitives</option>
                    <option>Croissance trop rapide pour suivre</option>
                    <option>Pas de visibilité / pilotage</option>
                    <option>Autre</option>
                  </select>
                </div>

                <div className="mb-8">
                  <label className="font-mono-overline text-mute mb-2 block">Nombre de collaborateurs</label>
                  <select data-testid="form-team-size" className="shin-input" required value={form.team_size} onChange={update("team_size")}>
                    <option value="">Sélectionner…</option>
                    <option>1 — 10</option>
                    <option>11 — 30</option>
                    <option>31 — 80</option>
                    <option>80+</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center disabled:opacity-60"
                  data-testid="form-submit"
                >
                  {loading ? "Envoi en cours…" : "Réserver mon diagnostic gratuit"}
                  {!loading && <ArrowUpRight size={16} />}
                </button>

                <p className="text-xs text-mute mt-4 text-center">
                  Sans engagement · Sans CB · Réponse sous 24h ouvrées
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
